import { createContext, FC, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { PERMISSIONS } from "../../constants/permissionKeys";
import { useHasPermission } from "../../hooks/useHasPermission";
import { formatAvailablePointsLabel, getPointsSummaryApi } from "../../service/points";
import { globalStore } from "../../utils/globalStore";

export interface ShellCreditsApi {
  /** 千分位格式化后的可用积分文案 */
  creditsText: string;
  /** 无积分权限时为 true（侧栏与顶栏都不展示积分，也不请求接口） */
  hidden: boolean;
  /** 手动触发重新拉取（任务完成等场景） */
  refresh: () => void;
}

export interface ShellCreditsProviderProps {
  children: ReactNode;
}

const ShellCreditsContext = createContext<ShellCreditsApi | null>(null);

/** 计算当前用户身份指纹，用户切换/角色变更时触发重新拉取积分 */
function userCreditsFingerprint(): string {
  const u = globalStore.getUserInfo() as
    | { id?: number; userId?: number; roles?: unknown[]; role?: string }
    | null;
  if (!u) return "";
  const roles = Array.isArray(u.roles) ? u.roles : [];
  const roleKeys = roles
    .map((x: unknown) =>
      typeof x === "string"
        ? x
        : String(
            (x as { role_key?: string; code?: string })?.role_key ??
              (x as { code?: string }).code ??
              ""
          )
    )
    .filter(Boolean)
    .join(",");
  return `${String(u.id ?? u.userId ?? "")}|${roleKeys}|${String(u.role ?? "")}`;
}

/**
 * 侧栏 + 顶栏共享的积分数据源：
 * 拉取 /points/wallet、监听 `points:refresh` 事件与 globalStore 变更。
 * 无 `points.view` 权限（含权限未就绪）时不展示、不请求。
 */
export const ShellCreditsProvider: FC<ShellCreditsProviderProps> = ({ children }) => {
  const hasPoints = useHasPermission(PERMISSIONS.POINTS_VIEW);
  /** 三态：null/false 均隐藏，避免权限未就绪时闪烁 */
  const hidden = hasPoints !== true;
  const [creditsText, setCreditsText] = useState("0");
  const creditsRequestSeqRef = useRef(0);

  const loadCredits = useCallback(async () => {
    const seq = ++creditsRequestSeqRef.current;
    try {
      const res = await getPointsSummaryApi();
      if (seq !== creditsRequestSeqRef.current) return;
      if (res.code === 0 && res.data) {
        setCreditsText(formatAvailablePointsLabel(res.data.availablePoints));
      } else {
        setCreditsText("0");
      }
    } catch {
      if (seq !== creditsRequestSeqRef.current) return;
      setCreditsText("0");
    }
  }, []);

  useEffect(() => {
    if (hidden) return;
    void loadCredits();
    const onRefresh = () => {
      void loadCredits();
    };
    window.addEventListener("points:refresh", onRefresh);
    let prevFp = userCreditsFingerprint();
    /** 跟踪 globalStore.membership 的引用，变化时直接读 availablePoint 同步积分展示 */
    let prevMembershipRef = globalStore.getMembership();
    const unsub = globalStore.subscribe(() => {
      const next = userCreditsFingerprint();
      if (next !== prevFp) {
        prevFp = next;
        void loadCredits();
      }
      // globalStore.membership 在 HomeShell 监听 membership:refresh 后会被刷新，
      // 这里直接读 availablePoint 同步侧栏积分，避免额外 /points/wallet 请求
      const m = globalStore.getMembership();
      if (m !== prevMembershipRef) {
        prevMembershipRef = m;
        if (m && typeof m.availablePoint === "number") {
          setCreditsText(formatAvailablePointsLabel(m.availablePoint));
        }
      }
    });
    return () => {
      window.removeEventListener("points:refresh", onRefresh);
      unsub();
    };
  }, [loadCredits, hidden]);

  const api = useMemo(
    () => ({ creditsText, hidden, refresh: loadCredits }),
    [creditsText, hidden, loadCredits]
  );

  return <ShellCreditsContext.Provider value={api}>{children}</ShellCreditsContext.Provider>;
};

export function useShellCredits(): ShellCreditsApi | null {
  return useContext(ShellCreditsContext);
}
